/**
 * 接口请求的浏览器计时（Resource Timing）
 *
 * 前端「实测」是 JS 发出请求到拿到结果的总时长，混着排队、建连、后端处理、下载，
 * 以及响应到达后因主线程繁忙 / 后台节流造成的回调延迟，单看总数分不清是谁慢。
 * 这里收集接口请求的浏览器计时，慢请求上报时按地址与发起时间对上号，拆出各段耗时。
 *
 * - 只保留最近 MAX_ENTRIES 条，不随运行时长增长；PerformanceObserver 也不受浏览器默认
 *   250 条计时缓冲区写满后停止记录的限制，长期挂机的页面同样拿得到。
 * - 跨域接口需响应头 Timing-Allow-Origin，浏览器才给出排队 / DNS / 建连 / 等后端响应 / 下载的细分；
 *   缺少时这些时间点为 0，只能给出网络总耗时与回调延迟。
 */

/** 保留最近多少条接口计时 */
const MAX_ENTRIES = 100;

/** 接口请求的发起方式：axios 走 XHR，keepalive 请求走 fetch */
const REQUEST_INITIATORS = new Set(['xmlhttprequest', 'fetch']);

let entries: PerformanceResourceTiming[] = [];
let observer: PerformanceObserver | null = null;

const keep = (list: PerformanceEntryList): void => {
  for (const entry of list as PerformanceResourceTiming[]) {
    if (REQUEST_INITIATORS.has(entry.initiatorType)) entries.push(entry);
  }
  if (entries.length > MAX_ENTRIES) entries = entries.slice(-MAX_ENTRIES);
};

export const setupRequestTimingCollector = (): void => {
  if (typeof PerformanceObserver === 'undefined') return;
  observer = new PerformanceObserver((list) => keep(list.getEntries()));
  observer.observe({ type: 'resource', buffered: true });
};

/**
 * 找某次请求对应的浏览器计时：地址以该路径结尾、且在 JS 发起之后开始的最早一条。
 * 响应拦截器执行时，本次请求的计时可能还在观察者队列里没派发，先 takeRecords 取出来。
 */
const findTiming = (url: string, sentAt: number): PerformanceResourceTiming | undefined => {
  if (observer) keep(observer.takeRecords());
  const path = url.split('?')[0] ?? url;
  return entries.find(
    (entry) => entry.startTime >= sentAt && new URL(entry.name).pathname.endsWith(path),
  );
};

const ms = (value: number): string => `${Math.max(0, Math.round(value))}ms`;

/**
 * 把一次请求的浏览器计时拆成告警字段。
 * url 为 axios 配置里的相对路径，sentAt 为请求拦截器里记录的 performance.now()。
 */
export const describeRequestTiming = (url: string, sentAt?: number): Record<string, string> => {
  if (sentAt === undefined) return {};
  const timing = findTiming(url, sentAt);
  if (!timing) return { 浏览器计时: '未采集到' };
  const result: Record<string, string> = {
    /** 请求真正发出到响应收完，排除了 JS 侧的等待 */
    网络耗时: ms(timing.responseEnd - timing.startTime),
    /** 响应已到达、JS 尚未处理的时间：主线程繁忙或页面在后台被节流时会明显变长 */
    回调延迟: ms(performance.now() - timing.responseEnd),
  };
  if (!timing.requestStart) {
    result.细分耗时 = '跨域未授权（接口需返回 Timing-Allow-Origin）';
    return result;
  }
  const dns = timing.domainLookupEnd - timing.domainLookupStart;
  const connect = timing.connectEnd - timing.connectStart;
  result.排队 = ms(timing.requestStart - timing.startTime - dns - connect);
  result.DNS = ms(dns);
  result.建连 = ms(connect);
  result.等后端响应 = ms(timing.responseStart - timing.requestStart);
  result.下载 = ms(timing.responseEnd - timing.responseStart);
  return result;
};
